import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Card,
  CardBody,
  Row,
  Col,
  Badge,
  Table,
  Button,
  Spinner,
  Progress,
  Container
} from "reactstrap";
import { getEnquiryDetailsApi } from "../../api/apiUrlConst";
import { InterestLevelMap, ResponseStatusMap } from "../../service/status";
import { useTheme } from "../../context/ThemeContext";
import ThemeCard from "../../Components/Common/ThemeCard";

/* ================= ENUM MAPPING ================= */

const EnquiryStatusMap: Record<string, { label: string; color: string }> = {
  New: { label: "New", color: "primary" },
  Assigned: { label: "Assigned", color: "warning" },
  "In Follow Up": { label: "In Follow Up", color: "info" },
  Joined: { label: "Joined", color: "success" },
  "Not Interested": { label: "Not Interested", color: "danger" },
  DND: { label: "DND", color: "dark" },
};

const AdminEnquiryDetails: React.FC = () => {
  const { eid } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  const { theme } = useTheme();
  const isDark = theme === "dark";


  useEffect(() => {
    if (eid) loadDetails();
  }, [eid]);

  const loadDetails = async () => {
    try {
      const res = await getEnquiryDetailsApi(eid);
      setData(res);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  if (loading)
    return (
      <div className="text-center mt-5">
        <Spinner />
      </div>
    );

  if (!data) return <div>No data found</div>;

  const enquiry = data.enquiry;
  const followUps = data.followUps || [];
  const fees = data.fees;
  const payments = data.payments || [];

const statusInfo =
  EnquiryStatusMap[enquiry.status] || {
    label: enquiry.status,
    color: "secondary"
  };

  const paidPercentage =
    fees && fees.totalFees
      ? (fees.paidAmount / fees.totalFees) * 100
      : 0;

  return (
    <Container fluid className="px-4 py-3" style={{
      minHeight: "100vh",
      background: isDark ? "#121212" : "#f8f9fc",
      color: isDark ? "#e9ecef" : "inherit"
    }}>

      {/* ================= HEADER ================= */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h3 className="fw-bold mb-0">Enquiry Profile</h3>
          <div className={`small ${isDark ? "text-secondary" : "text-muted"}`}>Complete enquiry overview</div>
        </div>
        <Button color={isDark ? "secondary" : "light"}
          className="shadow-sm" onClick={() => navigate(-1)}>
          ← Back
        </Button>
      </div>

      {/* ================= PROFILE CARD ================= */}
      <ThemeCard className="mb-4">
        <CardBody>
          <Row className="align-items-center">
            <Col md={8} className="d-flex align-items-center gap-3">
              <div
                className="rounded-circle bg-primary text-white d-flex align-items-center justify-content-center"
                style={{ width: 60, height: 60, fontSize: 20 }}
              >
                {enquiry.name?.charAt(0).toUpperCase()}
              </div>

              <div>
                <h4 className="fw-bold mb-1">{enquiry.name}</h4>
                <div className={`small ${isDark ? "text-secondary" : "text-muted"}`}>{enquiry.email}</div>
                <div className={`small ${isDark ? "text-secondary" : "text-muted"}`}>{enquiry.phoneNumber}</div>
              </div>
            </Col>

            <Col md={4} className="text-md-end mt-3 mt-md-0">
              <Badge color={statusInfo.color} pill className="px-3 py-2 fs-6">
                {statusInfo.label}
              </Badge>

              <div className="mt-2 small text-muted">
                EID: {enquiry.eid}
              </div>

              <div className="small mt-1">
                Coordinator:{" "}
                <strong>
                  {enquiry.coordinatorName || "Not Assigned"}
                </strong>
              </div>
            </Col>
          </Row>
        </CardBody>
      </ThemeCard>

      {/* ================= BASIC INFO ================= */}
      <ThemeCard className="mb-4">
        <CardBody>
          <h5 className="fw-bold mb-4 border-bottom pb-2">
            Course & Professional Details
          </h5>

          <Row className="gy-3">
            <Col md={6}>
              <div className={`small ${isDark ? "text-secondary" : "text-muted"}`}>Course</div>
              <div className="fw-semibold">{enquiry.courseName}</div>
            </Col>

            <Col md={6}>
              <div className={`small ${isDark ? "text-secondary" : "text-muted"}`}>Category</div>
              <div className="fw-semibold">{enquiry.courseCategory}</div>
            </Col>

            <Col md={6}>
              <div className={`small ${isDark ? "text-secondary" : "text-muted"}`}>Employer</div>
              <div className="fw-semibold">
                {enquiry.currentEmployer || "-"}
              </div>
            </Col>

            <Col md={6}>
              <div className={`small ${isDark ? "text-secondary" : "text-muted"}`}>Experience</div>
              <div className="fw-semibold">
                {enquiry.totalExperience
                  ? `${enquiry.totalExperience} years`
                  : "-"}
              </div>
            </Col>
          </Row>

          <hr />

          <div className={`small ${isDark ? "text-secondary" : "text-muted"}`}>Message</div>
          <div
            className="p-3 rounded-3 mt-2"
            style={{
              backgroundColor: isDark ? "#2a2a2a" : "#f8f9fa"
            }}
          >
            {enquiry.message || "No message provided"}
          </div>
        </CardBody>
      </ThemeCard>

      {/* ================= FOLLOW-UP TIMELINE ================= */}
      <ThemeCard className="mb-4">
        <CardBody>
          <h5 className="fw-bold mb-4 border-bottom pb-2">
            Follow-up Timeline
          </h5>

          {followUps.length === 0 && (
            <div className="text-muted">No follow-ups yet.</div>
          )}

 {followUps.map((f: any, index: number) => {

  const interestKey = f.interestLevel?.trim();
  const responseKey = f.responseStatus?.trim();

  const interest =
    InterestLevelMap[interestKey] || {
      label: interestKey || "Unknown",
      color: "secondary",
    };

  const response =
    ResponseStatusMap[responseKey] || {
      label: responseKey || "Unknown",
      color: "secondary",
    };

            return (
              <div key={index} className="position-relative ps-4 mb-4">

                {/* Timeline dot */}
                <div
                  style={{
                    width: 12,
                    height: 12,
                    background: isDark ? "#0dcaf0" : "#0d6efd",
                    borderRadius: "50%",
                    position: "absolute",
                    left: 0,
                    top: 6
                  }}
                />

                <div
                  className="p-3 rounded-3 shadow-sm"
                  style={{
                    backgroundColor: isDark ? "#2a2a2a" : "#f8f9fa",
                    color: isDark ? "#e9ecef" : "inherit"
                  }}
                >
                  <div className="d-flex justify-content-between align-items-center">
                    <div>
                      <strong>{f.createdByName}</strong>
                      <div className="small text-muted">
                        {new Date(f.followUpDate).toLocaleString()}
                      </div>
                    </div>

                    <div className="d-flex gap-2">
                      <Badge color={interest.color}>
                        {interest.label}
                      </Badge>
                      <Badge color={response.color}>
                        {response.label}
                      </Badge>
                    </div>
                  </div>

                  <div className="mt-3">{f.message}</div>

                  {f.nextContactDate && (
                    <div className="mt-2 small text-muted">
                      Next Contact:{" "}
                      {new Date(f.nextContactDate).toLocaleDateString()}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </CardBody>
      </ThemeCard>

      {/* ================= FEES SECTION ================= */}
      {fees && (
        <ThemeCard className="mb-4">
          <CardBody>
            <h5 className="fw-bold mb-4 border-bottom pb-2">
              Fees & Payments
            </h5>

            <Row className="mb-4">
              <Col md={3}>
                <div className="bg-light p-3 rounded-3 text-center shadow-sm">
                  <div className={`small ${isDark ? "text-secondary" : "text-muted"}`}>Total</div>
                  <div className="fw-bold fs-5">₹ {fees.totalFees}</div>
                </div>
              </Col>

              <Col md={3}>
                <div className="bg-success bg-opacity-10 p-3 rounded-3 text-center shadow-sm">
                  <div className={`small ${isDark ? "text-secondary" : "text-muted"}`}>Paid</div>
                  <div className="fw-bold fs-5 text-success">
                    ₹ {fees.paidAmount}
                  </div>
                </div>
              </Col>

              <Col md={3}>
                <div className="bg-danger bg-opacity-10 p-3 rounded-3 text-center shadow-sm">
                  <div className={`small ${isDark ? "text-secondary" : "text-muted"}`}>Pending</div>
                  <div className="fw-bold fs-5 text-danger">
                    ₹ {fees.pendingAmount}
                  </div>
                </div>
              </Col>

              <Col md={3}>
                <div className="bg-warning bg-opacity-10 p-3 rounded-3 text-center shadow-sm">
                  <div className={`small ${isDark ? "text-secondary" : "text-muted"}`}>Next Due</div>
                  <div className="fw-semibold">
                    {fees.nextDueDate
                      ? new Date(fees.nextDueDate).toLocaleDateString()
                      : "N/A"}
                  </div>
                </div>
              </Col>
            </Row>

            <Progress
              value={paidPercentage}
              color={fees.isFullyPaid ? "success" : "warning"}
              className="mb-4"
            >
              {paidPercentage.toFixed(0)}% Paid
            </Progress>

            <Table
              hover
              responsive
              dark={isDark}
              className="align-middle"
            >
              <thead className={isDark ? "" : "table-light"}>
                <tr>
                  <th>Date</th>
                  <th>Amount</th>
                  <th>Mode</th>
                  <th>Transaction ID</th>
                  <th>Account</th>
                </tr>
              </thead>
              <tbody>
                {payments.map((p: any, i: number) => (
                  <tr key={i}>
                    <td>
                      {new Date(p.paidDate).toLocaleDateString()}
                    </td>
                    <td className="fw-semibold">₹ {p.amount}</td>
                    <td>{p.mode}</td>
                    <td>{p.transactionId}</td>
                    <td>{p.account}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </CardBody>
        </ThemeCard>
      )}
    </Container>
  );
};

export default AdminEnquiryDetails;